import './OracleOnboarding.scss';
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MdAdd } from 'react-icons/md';
import {
  OnboardingStoreType,
  OracleOnboardingMultiDocumentSection,
  OracleOnboardingSectionKey
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Spinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { fullNameSelector } from '@src/redux/user';
import { useNotImplemented } from '@src/utils/hooks/useNotImplemented';
import { retrySummary } from './onboarding-api';
import { AddSourceFlow } from './components/AddSourceFlow';
import { MultiDocumentSectionCard } from './components/MultiDocumentSectionCard';
import { SingleDocumentSectionCard } from './components/SingleDocumentSectionCard';
import { ExternalStakeholdersSectionCard } from './components/ExternalStakeholdersSectionCard';
import { IconLabel } from './components/IconLabel';
import { StakeholdersSectionCard } from './components/StakeholdersSectionCard';
import { useInternalStakeholders } from './useInternalStakeholders';
import { isMultiDocumentSection } from './multi-document-utils';
import { useMultiDocuments } from './useMultiDocuments';
import {
  ONBOARDING_MENU_ITEMS,
  ONBOARDING_SECTIONS,
  STAKEHOLDERS_EXTERNAL_KEY,
  STAKEHOLDERS_INTERNAL_KEY
} from './oracle-onboarding-constants';
import {
  OnboardingCardState,
  onboardingSectionQueryKey,
  useOnboardingSections
} from './useOnboardingSections';

interface OracleOnboardingProps {
  company: string;
  store: OnboardingStoreType;
}

// A retry is in flight for one section in one store. The same section in the other store holds a
// different document, so its card must not show the spinner.
const retryKey = (store: OnboardingStoreType, sectionKey: OracleOnboardingSectionKey): string =>
  `${store}:${sectionKey}`;

export const OracleOnboarding = ({ company: cid, store }: OracleOnboardingProps) => {
  const { cardStates, isLoading, isError } = useOnboardingSections(cid, store);
  // These cards mount only after the sections resolve, so start their fetches here to run them in
  // parallel. Each card reads the same cache entry.
  useInternalStakeholders(cid);
  useMultiDocuments(cid, store, OracleOnboardingMultiDocumentSection.EXAMPLES_OF_EXCELLENCE);
  useMultiDocuments(cid, store, OracleOnboardingMultiDocumentSection.STANDARDS_AND_PRACTICES);
  const [addSourceFor, setAddSourceFor] = useState<OracleOnboardingSectionKey | null>(null);
  const queryClient = useQueryClient();
  const setError = useSetError();
  const userFullName = useAppSelector(fullNameSelector);

  // One entry per section with a retry in flight. Two sections can fail and be retried at the
  // same time, so a single key would clear the wrong card's spinner.
  const [retryingSections, setRetryingSections] = useState<ReadonlySet<string>>(new Set());

  // retrySummary returns once the pending version is queued. The invalidation refetches the
  // section, which restarts the idle poll -- and it runs on the rejected path too, where the
  // 404 and both 409s mean the cached card is already stale.
  const handleRetrySummary = useCallback(
    async (
      sectionKey: OracleOnboardingSectionKey,
      versionId: number,
      versionStore: OnboardingStoreType
    ) => {
      setRetryingSections(current => new Set(current).add(retryKey(versionStore, sectionKey)));
      try {
        // The version's own store, not the selected one.
        await retrySummary(cid, versionStore, versionId, userFullName);
      } catch (error) {
        setError({ message: `Couldn't retry the summary: ${error.message}`, reportError: error });
      } finally {
        await queryClient.invalidateQueries({
          queryKey: onboardingSectionQueryKey(cid, sectionKey, versionStore)
        });
        // Cleared after the refetch, so the card never offers Retry again over stale data.
        setRetryingSections(current => {
          const next = new Set(current);
          next.delete(retryKey(versionStore, sectionKey));
          return next;
        });
      }
    },
    [cid, queryClient, setError, userFullName]
  );

  return (
    <>
      <OnboardingBody
        isLoading={isLoading}
        isError={isError}
        company={cid}
        cardStates={cardStates}
        store={store}
        retryingSections={retryingSections}
        onAddSource={setAddSourceFor}
        onRetrySummary={handleRetrySummary}
      />
      {addSourceFor && (
        <AddSourceFlow
          company={cid}
          store={store}
          sectionKey={addSourceFor}
          onClose={() => setAddSourceFor(null)}
        />
      )}
    </>
  );
};

const OnboardingMenu = () => {
  const notImplemented = useNotImplemented();

  return (
    <nav className='onboarding-menu'>
      {ONBOARDING_MENU_ITEMS.map(({ key, label, icon }) => (
        <IconLabel key={key} icon={icon} label={label} />
      ))}
      <Button
        appearance={Appearance.EMPTY}
        design={Design.BRAND}
        size={Size.MD}
        leadingElement={<MdAdd />}
        onClick={notImplemented}
      >
        Add Section
      </Button>
    </nav>
  );
};

interface OnboardingBodyProps extends OnboardingSectionsProps {
  isLoading: boolean;
  isError: boolean;
}

const OnboardingBody = ({
  isLoading,
  isError,
  company,
  cardStates,
  store,
  retryingSections,
  onAddSource,
  onRetrySummary
}: OnboardingBodyProps) => {
  if (isLoading) {
    return <Spinner />;
  }
  if (isError) {
    // An undefined response derives to 'empty', which would render the cards under the error modal.
    return null;
  }
  return (
    <div className='oracle-onboarding-body'>
      <OnboardingMenu />
      <OnboardingSections
        company={company}
        cardStates={cardStates}
        store={store}
        retryingSections={retryingSections}
        onAddSource={onAddSource}
        onRetrySummary={onRetrySummary}
      />
    </div>
  );
};

interface OnboardingSectionsProps {
  company: string;
  cardStates: Record<OracleOnboardingSectionKey, OnboardingCardState>;
  store: OnboardingStoreType;
  retryingSections: ReadonlySet<string>;
  onAddSource: (sectionKey: OracleOnboardingSectionKey) => void;
  onRetrySummary: (
    sectionKey: OracleOnboardingSectionKey,
    versionId: number,
    versionStore: OnboardingStoreType
  ) => void;
}

const OnboardingSections = ({
  company,
  cardStates,
  store,
  retryingSections,
  onAddSource,
  onRetrySummary
}: OnboardingSectionsProps) => (
  <div className='onboarding-sections'>
    {ONBOARDING_SECTIONS.map(section => {
      if (section.key === STAKEHOLDERS_INTERNAL_KEY) {
        return (
          <StakeholdersSectionCard key={section.key} icon={section.icon} label={section.label} />
        );
      }
      if (section.key === STAKEHOLDERS_EXTERNAL_KEY) {
        return (
          <ExternalStakeholdersSectionCard
            key={section.key}
            icon={section.icon}
            label={section.label}
          />
        );
      }
      if (isMultiDocumentSection(section)) {
        return (
          <MultiDocumentSectionCard
            key={section.key}
            company={company}
            store={store}
            section={section}
          />
        );
      }
      const sectionKey = section.key;
      return (
        <SingleDocumentSectionCard
          key={sectionKey}
          icon={section.icon}
          label={section.label}
          emptyStateCopy={section.emptyStateCopy}
          addButtonLabel={section.addButtonLabel}
          state={cardStates[sectionKey]}
          isRetrying={retryingSections.has(retryKey(store, sectionKey))}
          onAddSource={() => onAddSource(sectionKey)}
          onRetrySummary={(versionId, versionStore) =>
            onRetrySummary(sectionKey, versionId, versionStore)
          }
        />
      );
    })}
  </div>
);
